import React from 'react';
import { isMobile } from 'react-device-detect';

import {
  BLUR_THRESHOLD,
  MOBILE_BLUR_THRESHOLD,
  VIDEO_INPUT
} from 'constants/camera';
import { KEY_DOWN_EVENT } from 'constants/events';
import {
  filter,
  first,
  get,
  isEmpty,
  isNull,
  map,
  some,
  zipObject
} from 'lodash';

import CameraSwitch from 'pages/Students/Assignments/Detail/components/CamerSwitch';
import ImageCropper from 'pages/Students/Assignments/Detail/components/ImageCropper';
import SubmitOverlay from 'pages/Students/Assignments/Detail/components/SubmitOverlay';
import CaptureControls from 'pages/Students/Assignments/Detail/components/WebcamControls';
import WebcamDisplay from 'pages/Students/Assignments/Detail/components/WebcamDisplay';
import { compressImage, fileToBase64 } from 'utils/files';
import { blobToImage, centerCrop, getCroppedImgBlob } from 'utils/files/images';
import { notifyError } from 'utils/notifications';
import {
  flipImageHorizontally,
  getBlurScore,
  getBlurScoreFromBase64,
  processMobileCapture,
  validateImageBlur
} from 'utils/webcam';

import styles from './styles.module.scss';

const SCANNING_VALIDATION_ENABLED =
  process.env.REACT_APP_SCANNING_VALIDATION_ENABLED === 'true';

class WebcamCapture extends React.Component {
  constructor(props) {
    super(props);

    this.webcamRef = React.createRef();
    this.canvasRef = React.createRef();

    this.lastShot = null;
    this.lastBlurScore = 0;
    this.animationFrameId = null;
  }

  state = {
    cameraError: null,
    qualityError: null,
    capturedImagesData: this.initializeImages(),
    crop: centerCrop({
      widthPercent: 80,
      heightPercent: 80
    }),
    croppedImageBlob: null,
    croppedImageUrl: null,
    croppingActive: false,
    sourceImage: null,
    startTimer: false,
    findBestFrame: false,
    previewImage: null,
    uploadedImageBlob: null, // storing the base64 of the image before the upload confirmation
    documentScanActive: false,
    documentScanCancelled: false
  };

  componentWillUnmount() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    window.removeEventListener(KEY_DOWN_EVENT, this.handleKeyDown);
  }

  async componentDidMount() {
    window.addEventListener(KEY_DOWN_EVENT, this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.code === 'Space') {
      e.preventDefault();
      const scanningFlow = isMobile
        ? this.capturePhotoMobile
        : this.startCapture;

      scanningFlow();
    }
  };

  initializeImages() {
    // Return value: { [task.id]: null }
    const { tasks } = this.props;

    return zipObject(map(tasks, 'id'), {
      image: null,
      submitting: false
    });
  }

  // Passes lower quality image source into detectBlur. If the current frame is
  // less blurry than previous frames, the program stores the source data for
  // the higher-quality image. Using the lower quality data for analysis alows us
  // to compute the blur in real time since it doesn't take as much computation.
  tick = () => {
    if (!this.webcamRef.current) {
      return;
    }

    // The timer can be reset. For example when the task has changed during the capture phase
    if (!this.state.startTimer) {
      this.resetBlurImageHelpers();
      return;
    }

    const imgBase64 = this.webcamRef.current.getScreenshot();

    const smallImage = document.createElement('img');

    smallImage.onload = () => {
      const blurScore = getBlurScoreFromBase64({
        image: smallImage
      });

      if (blurScore > this.lastBlurScore) {
        // Store the least blurry frame and its blur score in global variables
        this.lastBlurScore = blurScore;
        this.lastShot = imgBase64;
      }
    };

    smallImage.onerror = notifyError;

    smallImage.src = imgBase64;

    // analyze a new frame every 0.1 seconds
    setTimeout(() => {
      this.animationFrameId = requestAnimationFrame(this.tick);
    }, 100);
  };

  onUserMediaError = (error) => {
    this.setState({ cameraError: error });
  };

  changeTask = ({ step }) => {
    this.props.changeTask({ step }).then((activeTask) => {
      const previewImage = get(
        this.state.capturedImagesData,
        `${activeTask.id}.image`
      );

      this.setState(
        {
          previewImage,
          findBestFrame: false,
          startTimer: false,
          croppingActive: false
        },
        this.resetBlurImageHelpers
      );
    });
  };

  // This function is called when the student starts the timer.
  // It waits until the 1-second mark to start the blur detection.
  startCapture = () => {
    if (this.animationFrameId !== null) {
      return;
    }
    window.navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = filter(devices, { kind: VIDEO_INPUT });

      if (isEmpty(videoDevices) || !some(videoDevices, 'label')) {
        // Camera device is not allowed
        this.setState({ cameraError: { name: 'TrackStartError' } });
        return;
      }
    });

    this.setState({ startTimer: true });

    // Wait two seconds before looking for the best frame
    setTimeout(() => {
      this.setState({ findBestFrame: true }, () => {
        // Check https://css-tricks.com/using-requestanimationframe/ for more info.
        this.animationFrameId = requestAnimationFrame(this.tick);
      });
    }, 200);
  };

  capturePhotoMobile = () => {
    if (!this.webcamRef.current) {
      return;
    }

    const imgBase64 = this.webcamRef.current.getScreenshot();
    if (isNull(imgBase64)) {
      this.setState({ cameraError: { name: 'TrackStartError' } });
      return;
    }

    const finalImage = document.createElement('img');

    finalImage.onload = () => {
      // Crops the image if the there is overlapping
      const capturedImage = processMobileCapture({
        image: finalImage,
        webcamRect: this.webcamDiv.getBoundingClientRect(),
        navRect: this.taskNav.getBoundingClientRect(),
        topNavPos: 48
      });

      const validation = validateImageBlur({
        blurScore: this.lastBlurScore,
        threshold: MOBILE_BLUR_THRESHOLD
      });

      if (SCANNING_VALIDATION_ENABLED && !validation.isValid) {
        this.handleCaptureError({
          error: validation.error,
          previewImage: capturedImage
        });
        return;
      }

      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [this.props.activeTask.id]: {
            ...this.state.capturedImagesData[this.props.activeTask.id],
            image: capturedImage
          }
        },
        previewImage: capturedImage,
        croppingActive: true
      });
    };

    finalImage.onerror = notifyError;
    finalImage.src = imgBase64;
  };

  resetBlurImageHelpers = () => {
    this.lastShot = null;
    this.lastBlurScore = 0;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = null;
  };

  // This function is called when the user is capturing photos from a laptop. It is called
  // once the timer hits zero. It checks whether the final image must be mirrored and acts
  // accordingly. Then it saves the final image by calling setState.
  capturePhotoLaptop = () => {
    const finalImage = document.createElement('img');

    finalImage.onload = () => {
      const flippedImageData = flipImageHorizontally({ image: finalImage });

      if (SCANNING_VALIDATION_ENABLED && this.lastBlurScore < BLUR_THRESHOLD) {
        notifyError(
          'The image is too blurry. Please try again with a clearer image.'
        );
        this.setState(
          {
            capturedImagesData: {
              ...this.state.capturedImagesData
            },
            previewImage: flippedImageData.toDataURL(),
            qualityError: {
              name: 'BlurError',
              message: `The image is too blurry.`
            }
          },
          this.resetBlurImageHelpers
        );
      } else {
        this.setState(
          {
            capturedImagesData: {
              ...this.state.capturedImagesData,
              [this.props.activeTask.id]: {
                ...this.state.capturedImagesData[this.props.activeTask.id],
                image: flippedImageData.toDataURL()
              }
            },
            previewImage: flippedImageData.toDataURL(),
            croppingActive: true,
            qualityError: null
          },
          this.resetBlurImageHelpers
        );
      }
    };

    finalImage.onerror = notifyError;

    // this line triggers the finalImage.onload function
    finalImage.src = this.lastShot;
  };

  removeCapturedPhoto = () => {
    this.setState(
      {
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [this.props.activeTask.id]: {
            ...this.state.capturedImagesData[this.props.activeTask.id],
            image: null
          }
        },
        findBestFrame: false,
        startTimer: false,
        previewImage: null,
        croppingActive: false
      },
      this.resetBlurImageHelpers
    );
  };

  uploadCapturedPhoto = async () => {
    const { activeTask, submitting, uploadStudentWork } = this.props;
    const { crop, croppedImageBlob } = this.state;

    const activeTaskImg = get(
      this.state.capturedImagesData,
      `${activeTask.id}.image`
    );

    if (!activeTaskImg || !crop) return;

    // Set submitting state
    this.setState({
      capturedImagesData: {
        ...this.state.capturedImagesData,
        [activeTask.id]: {
          image: activeTaskImg,
          submitting: true
        }
      },
      findBestFrame: false,
      startTimer: false,
      previewImage: activeTaskImg,
      croppingActive: true
    });

    await uploadStudentWork(
      croppedImageBlob ? croppedImageBlob : activeTaskImg
    );

    // Stop submitting state
    this.setState(
      {
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            image: null,
            submitting: false
          }
        },
        findBestFrame: false,
        startTimer: false,
        previewImage: null,
        croppingActive: false
      },
      this.resetBlurImageHelpers
    );

    if (!submitting) {
      this.changeTask({ step: 1 });
    }
  };

  saveImageBlob = (files) => {
    compressImage(files[0], {
      maxSizeMB: 1,
      maxWidth: 1280,
      maxHeight: 720
    })
      .then((compressedBlob) =>
        blobToImage(compressedBlob).then((img) => {
          const blurScore = getBlurScore({ image: img });

          if (SCANNING_VALIDATION_ENABLED && blurScore < BLUR_THRESHOLD) {
            throw new Error('Image is too blurry, please retake.');
          }

          return fileToBase64(compressedBlob);
        })
      )
      .then((base64) => {
        this.setState({
          uploadedImageBlob: base64,
          previewImage: base64,
          croppingActive: true
        });
      })
      .catch(notifyError);
  };

  uploadSavedImageBlob = async () => {
    if (this.state.uploadedImageBlob) {
      const { activeTask } = this.props;

      // Set submitting state
      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            submitting: true
          }
        },
        croppingActive: false
      });

      await this.props.uploadStudentWork(this.state.uploadedImageBlob);

      // Stop submitting state
      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            submitting: false
          }
        },
        uploadedImageBlob: null,
        previewImage: null,
        croppingActive: false
      });
    }
  };

  removeSavedImageBlob = () => {
    if (this.state.uploadedImageBlob) {
      this.setState({
        uploadedImageBlob: null,
        previewImage: null,
        croppingActive: false
      });
    }
  };

  getTaskImagePreview = (task) => {
    const { capturedImagesData } = this.state;
    const imageData = get(capturedImagesData, task.id);
    const currentImgSrc = get(imageData, 'image');

    if (currentImgSrc) {
      return currentImgSrc;
    }

    return get(first(task.work), 'work_url');
  };

  handleCrop = async () => {
    const { sourceImage, crop } = this.state;

    if (!sourceImage || !crop?.width || !crop?.height) return;

    const blob = await getCroppedImgBlob({ image: sourceImage, crop });
    const url = URL.createObjectURL(blob);

    this.setState({
      croppedImageBlob: blob,
      croppedImageUrl: url,
      croppingActive: false
    });
  };

  handleCaptureError = ({ error, previewImage }) => {
    notifyError(error.message);
    this.setState(
      {
        previewImage,
        qualityError: error
      },
      this.resetBlurImageHelpers
    );
  };

  render() {
    const {
      activeTaskName,
      cameraError,
      capturedImagesData,
      crop,
      croppedImageUrl,
      croppingActive,
      documentScanActive,
      documentScanCancelled,
      startTimer,
      previewImage,
      qualityError,
      uploadedImageBlob
    } = this.state;
    const { activeTask, selectedCameraId, setSelectedCameraId } = this.props;

    const imageData = get(capturedImagesData, activeTask.id);
    const currentImgSubmitting = get(imageData, 'submitting');

    return (
      <div
        className={previewImage ? styles.webcamViewSetDims : styles.webcamView}
      >
        {!previewImage && (
          <CameraSwitch
            selectedCameraId={selectedCameraId}
            setSelectedCameraId={setSelectedCameraId}
          />
        )}

        <WebcamDisplay
          isMobile={isMobile}
          selectedCameraId={this.props.selectedCameraId}
          onUserMediaError={this.onUserMediaError}
          webcamRef={this.webcamRef}
          containerRef={(el) => (this.webcamDiv = el)}
          cameraError={cameraError}
          previewImage={previewImage}
        />

        <SubmitOverlay isSubmitting={currentImgSubmitting} />

        {(previewImage || uploadedImageBlob) && (
          <ImageCropper
            activeTaskName={activeTaskName}
            crop={crop}
            croppedImageUrl={croppedImageUrl}
            croppingActive={croppingActive}
            previewImage={previewImage}
            setCrop={(c) => this.setState({ crop: c })}
            setCroppedBlob={(imageBlob) =>
              this.setState({ croppedImageBlob: imageBlob })
            }
            setCroppedImageUrl={(imageUrl) =>
              this.setState({ croppedImageUrl: imageUrl })
            }
            setSourceImage={(sourceImage) => this.setState({ sourceImage })}
            uploadedImageBlob={uploadedImageBlob}
          />
        )}

        {!cameraError && (
          <CaptureControls
            isMobile={isMobile}
            previewImage={previewImage}
            uploadedImageBlob={uploadedImageBlob}
            croppingActive={croppingActive}
            startTimer={startTimer}
            documentScanActive={documentScanActive}
            documentScanCancelled={documentScanCancelled}
            activeTask={activeTask}
            taskImagePreview={this.getTaskImagePreview(activeTask)}
            qualityErrorMessage={qualityError ? qualityError.message : null}
            containerRef={(el) => (this.taskNav = el)}
            // Handlers
            onDrop={this.saveImageBlob}
            onCapture={isMobile ? this.capturePhotoMobile : this.startCapture}
            onTimerFinish={this.capturePhotoLaptop}
            onCrop={this.handleCrop}
            onRemovePhoto={this.removeCapturedPhoto}
            onUploadPhoto={this.uploadCapturedPhoto}
            onRemoveSavedBlob={this.removeSavedImageBlob}
            onUploadSavedBlob={this.uploadSavedImageBlob}
          />
        )}
      </div>
    );
  }
}

export default WebcamCapture;
